import React, { useEffect, useState } from 'react';
import useTranslation from '../hooks/useTranslation';
import Loading, { LoadingBall } from '@ingka/loading';
import Button, { ButtonType } from '@ingka/button';
import plusIcon from '@ingka/ssr-icon/paths/plus-small';
import QuantityDropdown from '@ingka/quantity-dropdown';
import Status from '@ingka/status';
import styled from 'styled-components';
import { useCartItemQuantityController } from '../hooks/useOrder';
import { useFindEquivalentSpareParts } from '../hooks/useFindEquivalentSpareParts';
import { Item, ItemProps } from './Item';
import { useCart, calculateNewNumberOfItems } from '../contexts/Cart';
import TMarkUp from './TMarkUp';
import { SparePart } from '../interfaces/Item';
import {
  AvailableOptionsWrapper,
  ItemFamily,
  ItemFamilyList,
} from './ItemFamilyStyles';
//import { sparePartService } from '../services';

/* globals CSS_CONFIG */

const AddQuantityDropdown = styled(QuantityDropdown)`
  margin-inline-end: 20px;
`;

interface FindEquivalentSparePartsProps {
  id: string;
  item: SparePart;
  itemFamily?: SparePart[];
  setSearch?: Function;
}

interface AddToCartTextButtonProps {
  loading: boolean;
  addToCart: () => void;
  maxNumberToSelect: number;
}

const FindEquivalentSpareParts: React.FC<FindEquivalentSparePartsProps> = ({
  id,
  ...rest
}): JSX.Element => {
  const { t } = useTranslation();
  const [items, isLoading, isError] = useFindEquivalentSpareParts(id);
  if (isLoading) {
    return (
      <Loading
        text={t('loading', {
          loading: t('spareParts.search.equivalentSpareParts'),
        })}
      >
        <LoadingBall />
      </Loading>
    );
  }

  if (isError) {
    return (
      <div>
        <TMarkUp value={t(`spareParts.search.noReplacementFound`)} />
      </div>
    );
  }
  const addDisplayItems = items.map((i, index): JSX.Element => {
    const { item, ...itemDisplayRest } = rest;
    return <AddItemDisplay key={index} item={i} {...itemDisplayRest} />;
  });
  return (
    <div>
      <h4 className="h4">
        <TMarkUp value={t(`spareParts.search.equivalentSpareParts`)} />
      </h4>
      <p>
        <TMarkUp value={t(`spareParts.search.guaranteedEquivalent`)} />
      </p>
      <hr />
      {items.length > 0 && <>{addDisplayItems}</>}
    </div>
  );
};
const StyledStatus = styled(Status)`
  margin-inline-end: 8px;
`;
interface ItemWithStatusProps extends ItemProps {
  status: string;
}
export const ErrorStatus: React.FC<ItemWithStatusProps> = ({
  item,
  status,
  children,
  ...rest
}): JSX.Element => (
  <Item item={item} showImageDot={true} enableOptions={true} {...rest}>
    <div style={{ display: 'flex' }}>
      <StyledStatus statusColor="red" />
      <div>
        <TMarkUp value={status} />
      </div>
    </div>
    {children}
  </Item>
);

const AddToCartButton: React.FC<AddToCartTextButtonProps> = ({
  loading,
  addToCart,
  maxNumberToSelect,
}): JSX.Element => {
  const { t } = useTranslation();
  const buttonProps = {
    type: 'primary' as ButtonType,
    small: true,
    loading,
    onClick: addToCart,
    disabled: !maxNumberToSelect,
    'aria-label': t(`spareParts.ariaLabel.addToCart`),
  };
  if (CSS_CONFIG.ADDTOCARTTEXTVISIBLE) {
    return (
      <Button
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...buttonProps}
        text={t(`spareParts.ariaLabel.addToCart`)}
      />
    );
  }
  return (
    <Button
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buttonProps}
      ssrIcon={plusIcon}
      iconOnly
    />
  );
};
export const EquivalentItemDisplay: React.FC<ItemWithStatusProps> = ({
  item,
  status,
  children,
  ...rest
}): JSX.Element => {
  return (
    <>
      <ErrorStatus item={item} status={status} children={children} {...rest} />
      <FindEquivalentSpareParts id={item.replacementId} item={item} {...rest} />
    </>
  );
};

export const AddItemDisplay: React.FC<ItemProps> = ({
  item,
  ...rest
}): JSX.Element => {
  const [{ loading, quantity }, { onQuantityChange, addToCart }] =
    useCartItemQuantityController(item);
  const { t } = useTranslation();
  const CartContext = useCart();
  const { id } = item;
  const [itemFamilyDisplay, setItemFamilyDisplay] = useState<any>();
  const [colorFlag, setColorFlag] = useState<boolean>(false);
  const {
    setSearch,
    onFamilyClick,
    onClickItem,
    onChangeSearch,
    onSetSelectedItem,
    refInput,
    itemFamily,
  } = rest;
  useEffect(() => {
    if (itemFamily && itemFamily.length > 0) {
      const hasColor = itemFamily.some((item) => item?.color);
      setColorFlag(hasColor);

      const filterItemArray = itemFamily
        .map((x: SparePart) =>
          x?.id != Number(id) && x?.replacementId != id.toString()
            ? x
            : undefined
        )
        .filter(Boolean);
      if (filterItemArray.length > 0)
        setItemFamilyDisplay([...filterItemArray]);
    }
  }, [item, itemFamily, id, setItemFamilyDisplay]);

  const maxNumberToSelect = calculateNewNumberOfItems(
    CartContext.getTotalNrOfItems(),
    item.stock,
    Math.min(item.stock, CSS_CONFIG.ITEM_LIMIT)
  );

  return (
    <div>
      <Item item={item} enableOptions={true} showImageDot={true} {...rest}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 'inherit' }}>
            <AddQuantityDropdown
              max={maxNumberToSelect}
              onChange={onQuantityChange}
              disabled={!maxNumberToSelect}
              value={Math.min(quantity, maxNumberToSelect)}
              ariaLabel={t('spareParts.ariaLabel.changeQuantity')}
            />
          </div>
          <div style={{ flex: 'inherit' }}>
            <AddToCartButton
              loading={loading}
              addToCart={addToCart}
              maxNumberToSelect={maxNumberToSelect}
            />
          </div>
        </div>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 'auto' }}>
            {/* {JSON.stringify(itemFamilyDisplay)} */}
            {/* {t('colors.grey-blue')} */}
            {/* <p>{t('spareParts.itemColorDescription')}</p> */}

            {colorFlag && (
              <AvailableOptionsWrapper role="note">
                <p>{t('spareParts.itemColorsText')}</p>
                {/* {itemFamilyDisplay.map((i: any) => {
                return <>{t('colors.' + [i.color.toLocaleLowerCase()])}</>;
              })} */}
                <ItemFamilyList>
                  {itemFamilyDisplay?.map((i: any) => {
                    return (
                      <>
                        <ItemFamily
                          key={i.id}
                          color={i.color}
                          aria-label={
                            i.color === null
                              ? ''
                              : t('spareParts.itemColorDescription') +
                                ' ' +
                                t('colors.' + [i.color.toLocaleLowerCase()])
                          }
                          onClick={() => {
                            onFamilyClick && onFamilyClick(i);
                            if (setSearch) {
                              setSearch(i.id);
                              onClickItem && onClickItem(i);
                              onChangeSearch && onChangeSearch(i.id);
                              onSetSelectedItem && onSetSelectedItem(i);
                              if (refInput?.current) {
                                const input =
                                  refInput.current.querySelector('input');
                                if (input) {
                                  input.value = i.id;
                                }
                              }
                            }
                          }}
                        />
                      </>
                    );
                  })}
                </ItemFamilyList>
              </AvailableOptionsWrapper>
            )}
          </div>
        </div>
      </Item>
    </div>
  );
};

export const ShowStatusItem: React.FC<ItemProps> = ({
  item,
  ...rest
}): JSX.Element => {
  const { t } = useTranslation();
  const getColorOptions = (itemFamily: any | undefined) => {
    // Log the itemFamily array to the console
    if (itemFamily?.length == 1 && !itemFamily[0].color) {
      return;
    }

    return itemFamily && itemFamily.length > 0 ? (
      <AvailableOptionsWrapper role="note">
        <p>{t('spareParts.itemColorsText')}</p>
        <ItemFamilyList>
          {itemFamily?.map((i: any) => {
            return (
              <ItemFamily
                key={i.id}
                color={i.color}
                aria-label={
                  i.color === null
                    ? ''
                    : t('spareParts.itemColorDescription') +
                      ' ' +
                      t('colors.' + [i.color.toLocaleLowerCase()])
                }
                onClick={() => {
                  rest.onFamilyClick && rest.onFamilyClick(i);
                  if (rest.setSearch) {
                    rest.setSearch(i.id);
                    rest.onClickItem && rest.onClickItem(i);
                    rest.onChangeSearch && rest.onChangeSearch(i.id);
                    rest.onSetSelectedItem && rest.onSetSelectedItem(i);
                    if (rest.refInput?.current) {
                      const input =
                        rest.refInput.current.querySelector('input');
                      if (input) {
                        input.value = i.id;
                      }
                    }
                  }
                }}
              />
            );
          })}
        </ItemFamilyList>
      </AvailableOptionsWrapper>
    ) : null;
  };

  const filterItemArray = rest.itemFamily
    ?.map((x: SparePart) =>
      x?.id != Number(item.id) && x?.replacementId != item.id.toString()
        ? x
        : undefined
    )
    .filter(Boolean);

  switch (item.status) {
    case 'STOCK_NOT_RECEIVED':
    case 'ORDER_AT_CSC':
      return (
        <ErrorStatus
          item={item}
          status={t('spareParts.status.orderAtCSC')}
          {...rest}
        >
          {getColorOptions(filterItemArray)}
        </ErrorStatus>
      );
    case 'NOT_AVAILABLE':
      return (
        <ErrorStatus
          item={item}
          status={t('spareParts.status.notAvailable')}
          {...rest}
        >
          {getColorOptions(filterItemArray)}
        </ErrorStatus>
      );
    case 'DISCONTINUED_WITH_REPLACEMENTID':
      return (
        <EquivalentItemDisplay
          item={item}
          status={t('spareParts.status.discontinued')}
          {...rest}
        >
          {getColorOptions(filterItemArray)}
        </EquivalentItemDisplay>
      );
    case 'OUT_OF_STOCK':
      return (
        <EquivalentItemDisplay
          item={item}
          status={t('spareParts.status.outOfStock')}
          {...rest}
        >
          {getColorOptions(filterItemArray)}
        </EquivalentItemDisplay>
      );
    default:
      return <AddItemDisplay item={item} {...rest} />;
  }
};

export const SelectedItem: React.FC<ItemProps> = ({
  item,
  ...rest
}): JSX.Element | null => {
  if (!item) {
    return null;
  }

  return <ShowStatusItem item={item} {...rest} />;
};
